import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getItem, setItem } from "@lib/storage";
import { check, request, PERMISSIONS, RESULTS } from "react-native-permissions";
import { getCurrentLocation as fetchDeviceLocation } from "@utils/getCurrentLocation";
import { actions as locationActions } from "@reducers/locationReducer";

const MILLISECONDS_IN_SECONDS = 1000 * 60;

/**
 * THE shape every location read resolves to, from either hook, on every path —
 * cache hit, fresh capture, stale, empty, permission denied, or outright
 * failure. Callers destructure the same four keys unconditionally and never
 * branch on shape; an unavailable value is `null`, never a missing key.
 *
 * A fresh object each call, never a shared constant: a caller that mutates
 * what it got back must not be able to poison later reads.
 */
const emptyFix = () => ({
  latitude: null,
  longitude: null,
  accuracy: null,
  updated_at: null,
});

/**
 * Normalise any internal coordinate carrier into that shape. Accepts the
 * store's `updated_at` and the MMKV entry's `created_at` alike. Coordinates are
 * all-or-nothing: a half-populated source (one axis null) degrades to the empty
 * fix rather than shipping a half-valid point.
 */
const toFix = (source) => {
  if (source?.latitude == null || source?.longitude == null) return emptyFix();
  return {
    latitude: source.latitude,
    longitude: source.longitude,
    accuracy: source.accuracy ?? null,
    updated_at: source.updated_at ?? source.created_at ?? null,
  };
};

/** Same calendar day, device-local. */
const isFromToday = (timestamp) => {
  if (!timestamp) return false;
  const then = new Date(timestamp);
  if (Number.isNaN(then.getTime())) return false;
  return then.toDateString() === new Date().toDateString();
};

const useLocationHook = () => {
  const [coordinates, setCoordinates] = useState(null);
  const dispatch = useDispatch();

  const saveCoordinates = async (coords) => {
    try {
      await setItem(
        "userCoordinates",
        JSON.stringify({ ...coords, created_at: new Date() }),
      );
      setCoordinates({ ...coords, created_at: new Date() });
    } catch (error) {
      console.error("Error saving coordinates to storage:", error);
    }
  };

  const getCurrentLocation = (timeout = 1) =>
    new Promise(async (resolve, reject) => {
      const value = await getItem("userCoordinates");
      const coords = JSON.parse(value);
      if (
        coords &&
        new Date() - new Date(coords.created_at) <
          MILLISECONDS_IN_SECONDS * timeout
      ) {
        return resolve(toFix(coords));
      }
      if (
        coordinates &&
        new Date() - new Date(coordinates.created_at) <
          MILLISECONDS_IN_SECONDS * timeout
      ) {
        return resolve(toFix(coordinates));
      }
      try {
        const position = await fetchDeviceLocation();
        const { latitude, longitude, accuracy } = position.coords;
        // One timestamp for the resolved value, so the caller's updated_at
        // matches what the caches were told rather than drifting a few ms.
        const capturedAt = new Date().toISOString();
        saveCoordinates({ latitude, longitude });
        // Publish every real capture to the session-scoped slice too, so a
        // screen that took a fix keeps the store fresh for cache-only readers
        // (useCachedLocation) on other screens.
        dispatch(
          locationActions.locationFixReceived({
            latitude,
            longitude,
            accuracy,
            source: "capture",
          }),
        );
        resolve(
          toFix({ latitude, longitude, accuracy, updated_at: capturedAt }),
        );
      } catch (error) {
        console.log("Error getting location:", error);
        resolve(emptyFix());
      }
    });

  const checkPermission = async () => {
    try {
      const result = await check(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
      if (result === RESULTS.GRANTED) {
        await getCurrentLocation();
      } else if (result === RESULTS.DENIED) {
        const requestResult = await request(
          PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION,
        );
        if (requestResult === RESULTS.GRANTED) {
          await getCurrentLocation();
        } else {
          //   navigation.navigate(permissionDeniedScreen);
        }
      } else {
        // navigation.navigate(permissionDeniedScreen);
      }
    } catch (error) {
      console.error("Permission check error:", error);
    }
  };

  useEffect(() => {
    checkPermission();
  }, []);

  return getCurrentLocation;
};

/**
 * Cache-only variant of `useLocationHook`.
 *
 * NEVER reaches `MovexGeo.captureCurrentPosition` — no GPS wake-up, no
 * permission prompt on mount, no multi-second wait, no chance of the capture
 * hanging. Reads exactly one source: `state.location.lastFix`, the
 * session-scoped slice that is NOT redux-persisted (see
 * `reducers/locationReducer.js`). Fed by both the movex.geo tracking stream
 * and any one-shot capture another screen takes, whichever happened last.
 *
 * Deliberately does NOT fall back to `auth.coords` or the `userCoordinates`
 * MMKV entry. Both survive a restart, so on a cold start they hand back the
 * previous session's position looking exactly like a current one — a DB who
 * force-closes the app and reopens it at the doorstep would stamp the delivery
 * with yesterday's coordinates. If it wasn't observed by this process, it
 * isn't used.
 *
 * Guarded to TODAY (device-local calendar day) so a process left alive
 * overnight can't serve yesterday's last fix either. No tighter maximum age is
 * enforced — the caller has opted into "whatever is cached", so a fix from
 * earlier today is still returned rather than triggering a capture, and
 * `updated_at` comes back alongside the coordinates for any caller that wants
 * to judge for itself. Note movex.geo's motion FSM gates GPS while the device
 * is STATIONARY (~5 min idle), so even the live stream goes stale while a DB
 * stands at a door — spatially it stays near the point where they stopped.
 *
 * Always resolves the canonical `{latitude, longitude, accuracy, updated_at}`
 * shape — all four keys present on every path. When nothing qualifies (tracking
 * never started this session, no capture taken, or the only fix is from a
 * previous day) every value is `null`, identical to what a failed capture
 * resolves with, so callers destructure unconditionally and never branch on
 * shape.
 *
 * Returns a stable, synchronous getter — safe to `await` at existing
 * callsites that expect the promise-based `getCurrentLocation`.
 */
export const useCachedLocation = () => {
  const lastFix = useSelector((state) => state.location?.lastFix);
  // Read through a ref so the returned getter stays referentially stable and
  // never goes stale inside long-lived async closures (image-upload callbacks).
  const lastFixRef = useRef(lastFix);
  lastFixRef.current = lastFix;

  return useCallback(() => {
    const fix = lastFixRef.current;
    // Stale-day and empty-store both degrade to the same all-null fix as a
    // failed capture — one shape out of every path, in both hooks.
    if (!fix || !isFromToday(fix.updated_at)) return emptyFix();
    return toFix(fix);
  }, []);
};

export default useLocationHook;
